import { find } from 'lodash';

import {
  notificationIsFetching,
  notificationShouldBeVisible,
} from '../selector';
import { doRequestDismissNotifications } from '../../notification/actions';
import Validation from '../../../utils/Validation';
import {
  NOONA_NAVIGATION_SERVICES_NOTIFICATION,
  NOONA_PAGE_NOTIFICATION,
} from '../../../definitions/NotificationConstants';
import { doPushToast } from '../../toasts/actions';
import { receiveServices } from '../actions';
import {
  compareStatus,
  NOONA,
  PYYDETTY,
} from '../../../definitions/NoonaConstants';
import ActionError from '../../ActionError';
import Logger from '../../../utils/Logger';
import { BACKENDNOTIFICATIONS } from '../../reducer';
import {
  NOONA_PASSWORD_RENEWAL_FAILED_TOAST,
  NOONA_PASSWORD_RENEWAL_SUCCESS_TOAST,
} from '../../../definitions/ToastConstants';

let { id: module_id } = module;
const logger = new Logger(module_id);

const createActionName =
  require('../../util').createActionName('services/noona');

/// PHONE NUMBER EDITING

export const INIT_PHONE_NUMBER = createActionName('INIT_PHONE_NUMBER');
export const EDIT_PHONE_NUMBER = createActionName('EDIT_PHONE_NUMBER');
export const EDIT_PHONE_NUMBER_CONFIRMATION = createActionName(
  'EDIT_PHONE_NUMBER_CONFIRMATION'
);
export const PHONE_NUMBER_VALIDATION_ERROR = createActionName(
  'PHONE_NUMBER_VALIDATION_ERROR'
);
export const RESET_PHONE_NUMBER_VALIDATION_ERRORS = createActionName(
  'RESET_PHONE_NUMBER_VALIDATION_ERRORS'
);

/**
 * Action for editing value in phone number check of noona activation
 * @param {String} currentValue User's input value in the phone number field.
 */
export const editPhoneNumber = (currentValue) => ({
  type: EDIT_PHONE_NUMBER,
  payload: { currentValue },
});

/**
 * Action for editing value in phone number confirmation check of noona activation
 * @param {String} confirmValue User's input value in the phone number confirm field.
 */
export const editPhoneNumberConfirmation = (confirmValue) => ({
  type: EDIT_PHONE_NUMBER_CONFIRMATION,
  payload: { confirmValue },
});

/**
 * Action for receiving validation errors in the activation phase (phone number).
 * Could this be merged with errorNoonaAccountActivation?
 * */
export const phoneNumberValidationError = (errors) => ({
  type: PHONE_NUMBER_VALIDATION_ERROR,
  payload: { errors },
});

/**
 * Action for resetting validation errors in the activation phase (phone number).
 * */
export const resetPhoneNumberValidationErrors = () => ({
  type: RESET_PHONE_NUMBER_VALIDATION_ERRORS,
});

/// ACCOUNT ACTIVATION

export const ACCOUNT_ACTIVATION_INIT = createActionName(
  'ACCOUNT_ACTIVATION_INIT'
);
export const ACCOUNT_ACTIVATION_CANCEL = createActionName(
  'ACCOUNT_ACTIVATION_CANCEL'
);
export const ACCOUNT_ACTIVATION_ERROR = createActionName(
  'ACCOUNT_ACTIVATION_ERROR'
);
export const ACCOUNT_ACTIVATION_REQUEST = createActionName(
  'ACCOUNT_ACTIVATION_REQUEST'
);
export const ACCOUNT_ACTIVATION_RECEIVE = createActionName(
  'ACCOUNT_ACTIVATION_RECEIVE'
);

/**
 * Action for initializing noona account activation (user pushes "Aktivoi tili" -button the first time.
 * @param {String} originalValue Original value of the phone number before editing.
 */
export const initNoonaAccountActivation = (originalValue) => ({
  type: ACCOUNT_ACTIVATION_INIT,
  payload: { originalValue },
});

/**
 * Action for canceling noona account activation.
 */
export const cancelNoonaAccountActivation = () => ({
  type: ACCOUNT_ACTIVATION_CANCEL,
});

/**
 * Api action for requesting noona account activation from backend.
 * The api call is performed in activateAccount -action.
 */
export const requestNoonaAccountActivation = () => ({
  type: ACCOUNT_ACTIVATION_REQUEST,
});

/**
 * Api action for receiveing noona account activation result from backend.
 * Data should return only result whether account activation could be sent.
 * The api call is performed in activateAccount -action.
 * @param {String} data Data received from backend.
 * */
export const receiveNoonaAccountActivation = (data) => ({
  type: ACCOUNT_ACTIVATION_RECEIVE,
  payload: { data },
});

/**
 * Api action for situation when noona account activation fails.
 * Failure can be from backend integrations or frontend and error type should
 * be set in the backend/api layer and checked accordingly
 * @param {String} error Error received from backend/api layer.
 */
export const errorNoonaAccountActivation = (error) => ({
  type: ACCOUNT_ACTIVATION_ERROR,
  payload: { error },
});

const validateNoonaAccountActivation = (state) => {
  let { changed, currentValue, confirmValue } = state;
  logger.debug(
    `validating phone numbers: changed: ${changed}, current value ${currentValue}, confirmValue. ${confirmValue}`
  );
  if (changed) {
    logger.debug('validating phone');
    return Validation.validatePhone(currentValue, confirmValue);
  } else {
    logger.debug('validating phone without confirm');
    return Validation.validatePhoneWithoutConfirm(currentValue);
  }
};

/**
 * Api action for handling use case of patient requesting activation
 * after checking and entering confirmation phone number.
 * This action also includes validating phone number and confirm value.
 * Activation will be requested from backend, and subsequent actions
 * are sent accordingly.
 *
 * This is a noona specific action since account activation may be different
 * depending on service integration.
 */
export const activateNoonaAccount = () => (dispatch, getState, services) => {
  return new Promise((resolve, reject) => {
    let integrations = getState().services.integrations.data;
    let noonaIntegration = find(integrations, (item) => item.name === NOONA);

    if (!compareStatus(noonaIntegration.status, PYYDETTY)) {
      // Check that noona actually is in the required state.
      // If not, throw error.
      return reject(
        dispatch(
          errorNoonaAccountActivation(
            new ActionError(
              new Error(`Wrong noona status: ${noonaIntegration.status}`)
            )
          )
        )
      );
    }

    // validate  phone number
    dispatch(resetPhoneNumberValidationErrors());
    let errors = validateNoonaAccountActivation(
      getState().services.noona.phoneNumber
    );

    if (errors.phone || errors.phoneConfirm) {
      logger.debug('found phone errors, ', { errors });
      return reject(dispatch(phoneNumberValidationError(errors)));
    }

    let phoneNumber = getState().services.noona.phoneNumber.currentValue;
    let payload = { payload: phoneNumber };

    dispatch(requestNoonaAccountActivation());
    services.api
      .requestAccount(payload)
      .then((data) => {
        let currentServicesWithoutNoona =
          getState().services.integrations.data.filter(
            (item) => item.name !== NOONA
          );
        dispatch(
          receiveServices([
            ...currentServicesWithoutNoona,
            { name: NOONA, status: data },
          ])
        );
        return resolve(dispatch(receiveNoonaAccountActivation(data)));
      })
      .catch((error) => {
        return reject(dispatch(errorNoonaAccountActivation(error)));
      });
  });
};

export const automaticallyDismissNoonaActivationNotification =
  () => (dispatch, getState) => {
    let state = getState();
    let { data: notificationData } = state[BACKENDNOTIFICATIONS];

    if (
      notificationShouldBeVisible(NOONA_NAVIGATION_SERVICES_NOTIFICATION)(
        notificationData
      ) &&
      !notificationIsFetching(NOONA_PAGE_NOTIFICATION)(notificationData)
    ) {
      logger.debug('dispatching dismiss service activation notification');
      dispatch(
        doRequestDismissNotifications([NOONA_NAVIGATION_SERVICES_NOTIFICATION])
      );
    }
  };

/// PASSWORD RENEWAL

export const PASSWORD_RENEWAL_MODAL_VISIBILITY = createActionName(
  'PASSWORD_RENEWAL_MODAL_VISIBILITY'
);
export const PASSWORD_RENEWAL_REQUEST = createActionName(
  'PASSWORD_RENEWAL_REQUEST'
);
export const PASSWORD_RENEWAL_RECEIVE = createActionName(
  'PASSWORD_RENEWAL_RECEIVE'
);
export const PASSWORD_RENEWAL_ERROR = createActionName(
  'PASSWORD_RENEWAL_ERROR'
);

export const passwordRenewalModalVisibility = (modalVisible) => ({
  type: PASSWORD_RENEWAL_MODAL_VISIBILITY,
  payload: { modalVisible },
});

export const passwordRenewalRequest = () => ({
  type: PASSWORD_RENEWAL_REQUEST,
});

export const passwordRenewalReceive = () => ({
  type: PASSWORD_RENEWAL_RECEIVE,
});
export const passwordRenewalError = (error) => ({
  type: PASSWORD_RENEWAL_ERROR,
  payload: { error },
});

export const doPasswordRenewalRequest =
  () => (dispatch, getState, services) => {
    return new Promise((resolve, reject) => {
      try {
        dispatch(passwordRenewalRequest());
        services.api
          .requestPassword()
          .then(() => {
            dispatch(passwordRenewalModalVisibility(false));
            dispatch(doPushToast(NOONA_PASSWORD_RENEWAL_SUCCESS_TOAST()));
            resolve(dispatch(passwordRenewalReceive()));
          })
          .catch((error) => {
            dispatch(passwordRenewalModalVisibility(false));
            dispatch(doPushToast(NOONA_PASSWORD_RENEWAL_FAILED_TOAST()));
            reject(dispatch(passwordRenewalError(error)));
          });
      } catch (error) {
        dispatch(passwordRenewalModalVisibility(false));
        dispatch(doPushToast(NOONA_PASSWORD_RENEWAL_FAILED_TOAST()));
        reject(dispatch(passwordRenewalError(new ActionError(error))));
      }
    });
  };
